/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-study-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import { UserContext } from 'contexts/user-context';
import { useBoolean, useTranslation } from 'hooks';
import { OrganisationProgramFields } from 'queries/generated/fragments.generated';
import { GET_MANAGE_ORG_STUDIES, SAVE_STUDY, SAVE_STUDY_CONFIGURATION, SET_STUDY_LOGO } from 'queries/studies';
import { GET_ORGANISATION } from 'queries/uam';
import Constants from 'utils/constants';
import NotificationService from 'utils/notification';
import { checkConfigIdErrors, checkIfNameExist } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import CopyButton from 'views/components/core/copy-button';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import EditLogo from 'views/components/core/edit-logo';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

import './styles.scss';
import { ControlsAutomationColor, StudyAutomationColor } from '@types/graphql-global-types';
import { useGetStudyHasDataset } from '@apis/bff-api/central-monitoring';

interface AddStudyDialogInterface {
    open: boolean;
    close(action: string | boolean): any;
    currentStudy?: any;
    programs: OrganisationProgramFields[];
    organisationId: string | null;
    isSysAdmin: boolean;
    studies?: any[];
}

const AddStudyDialog = ({
    open,
    close,
    currentStudy,
    programs,
    organisationId,
    studies,
    isSysAdmin,
}: AddStudyDialogInterface) => {
    const { t } = useTranslation();
    const user = useContext(UserContext);

    const initialState = {
        id: null,
        configId: null,
        studyNumber: '',
        shortName: '',
        title: '',
        sponsor: '',
        logo: '',
        program: '',
        studyId: '',
        useLatestCentralMonitoring: true,
    };
    const initialError = { studyNumber: '', shortName: '', title: '', sponsor: '', configId: '' };
    const [state, setState] = useState({ ...initialState });
    const [error, setError] = useState({ ...initialError });
    const disabledSaveButton = useBoolean(true);
    const submitted = useBoolean(false);
    const changesWereMade = useBoolean(false);
    const [programDataArray, setProgramDataArray] = useState([]);
    const [saveStudy] = useMutation(SAVE_STUDY);
    const [saveStudyConfiguration] = useMutation(SAVE_STUDY_CONFIGURATION);
    const [saveStudyLogo] = useMutation(SET_STUDY_LOGO);

    const { data: studyHasDataset } = useGetStudyHasDataset(currentStudy?.id);

    useEffect(() => {
        if (currentStudy) {
            setState({
                id: currentStudy.id,
                configId: currentStudy.configId,
                studyNumber: currentStudy.studyNumber || '',
                shortName: currentStudy.shortName || '',
                title: currentStudy.title || '',
                sponsor: currentStudy.sponsor || '',
                logo: currentStudy.logo,
                program: currentStudy.programsIds?.length ? currentStudy.programsIds[0] : Constants.ObjectId.Empty,
                studyId: currentStudy.id,
                useLatestCentralMonitoring: currentStudy.useLatestCentralMonitoring,
            });
        } else setState({ ...initialState });
    }, [currentStudy]);

    useEffect(() => {
        if (open) {
            const newProgramArray =
                programs?.map((program) => ({ id: program.id, text: program.name, active: true })) || [];
            newProgramArray.unshift({ id: Constants.ObjectId.Empty, text: t('admin:selectProgram'), active: true });
            setProgramDataArray(newProgramArray);
        }
    }, [programs, open]);

    const onClose = (action?: string | boolean) => {
        disabledSaveButton.setTrue();
        submitted.setFalse();
        setState({ ...initialState });
        setError({ ...initialError });
        close(action);
    };

    const checkForName = () => {
        const value = {
            ...state,
            studyNumber: state.studyNumber.trim(),
            id: currentStudy?.id ?? null,
        };

        return checkIfNameExist(value, studies, 'studyNumber');
    };

    const validation = (checkRequired: boolean) => {
        let isValid = true;
        let nameError = '';
        let shortNameError = '';
        let descriptionError = '';
        let sponsorError = '';
        let configIdError = '';

        if (currentStudy) {
            configIdError = checkConfigIdErrors(state, studies || [], state.configId || undefined) || '';
        }

        if (!state.studyNumber && checkRequired) {
            isValid = false;
            nameError = t('ra:requiredFieldError');
        }

        if (state.studyNumber.length > 20) {
            isValid = false;
            nameError = `${t('limitExceededBy')} ${Math.abs(state.studyNumber.length - 20)} ${t('characters')}`;
        }

        if (state.studyNumber && checkForName()) {
            isValid = false;
            nameError = t('admin:studyAlreadyExists');
        }

        if (!state.shortName && checkRequired) {
            isValid = false;
            shortNameError = t('ra:requiredFieldError');
        }

        if (state.shortName.length > 70) {
            isValid = false;
            shortNameError = `${t('limitExceededBy')} ${Math.abs(state.shortName.length - 70)} ${t('characters')}`;
        }

        if (!state.title && checkRequired) {
            isValid = false;
            descriptionError = t('ra:requiredFieldError');
        }

        if (state.title.length > 500) {
            isValid = false;
            descriptionError = `${t('limitExceededBy')} ${Math.abs(state.title.length - 500)} ${t('characters')}`;
        }

        if (state.sponsor.length > 70) {
            isValid = false;
            sponsorError = `${t('limitExceededBy')} ${Math.abs(state.sponsor.length - 70)} ${t('characters')}`;
        }

        setError({
            studyNumber: nameError,
            shortName: shortNameError,
            title: descriptionError,
            sponsor: sponsorError,
            configId: configIdError,
        });

        return isValid;
    };

    useEffect(() => {
        if (open) validation(submitted.value);
    }, [state]);

    const onChange = (fieldName: string) => (event: any) => {
        changesWereMade.setTrue();
        if (disabledSaveButton.value) disabledSaveButton.setFalse();
        setState({ ...state, [fieldName]: event.target.value });
    };

    const onLogoChange = (base64Src: string) => {
        if (disabledSaveButton.value) disabledSaveButton.setFalse();
        setState({ ...state, logo: base64Src });
    };

    const successRequest = () => {
        if (currentStudy)
            NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: currentStudy.recordId }));
        else NotificationService.showNotification(t('admin:studySuccessfulCreate'));
        onClose();
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const getRefetchQueries = () => {
        if (!currentStudy)
            return [
                { query: GET_MANAGE_ORG_STUDIES },
                {
                    query: GET_ORGANISATION,
                    variables: {
                        id: organisationId,
                    },
                },
            ];
        return [];
    };

    const submit = () => {
        submitted.setTrue();
        if (validation(true) && !checkForName()) {
            disabledSaveButton.setTrue();
            if (!changesWereMade.value)
                saveStudyLogo({
                    variables: {
                        studyId: currentStudy.id,
                        logo: state.logo?.substring(state.logo.lastIndexOf(',') + 1) || '',
                    },
                    refetchQueries: getRefetchQueries(),
                })
                    .then(() => {
                        NavigationService.setUpdateStudies(user?.user.organisationKey, true);
                        successRequest();
                    })
                    .catch(() => {
                        errorRequest();
                    });
            else {
                let mutation;
                let variables;

                if (!currentStudy) {
                    // Use the SAVE_STUDY mutation to create a new study
                    mutation = saveStudy;
                    variables = {
                        studyId: null,
                        study: {
                            id: state.id,
                            configId: state.configId,
                            studyNumber: state.studyNumber,
                            shortName: state.shortName,
                            title: state.title,
                            programs: state.program.length ? [state.program] : [],
                            products: null,
                            indications: null,
                            sponsor: state.sponsor,
                            useLatestCentralMonitoring: state.useLatestCentralMonitoring,
                            automationActive: false,
                            studyAutomationColor: StudyAutomationColor.NONE,
                            controlsAutomationActive: false,
                            controlsAutomationColor: ControlsAutomationColor.NONE,
                            integrationActive: false,
                            integrationStudyNumber: '',
                        },
                        reason: '',
                    };
                } else {
                    // Use the SAVE_STUDY_CONFIGURATION to update a study
                    mutation = saveStudyConfiguration;
                    variables = {
                        studyId: currentStudy.id,
                        configId: state.configId,
                        studyNumber: state.studyNumber,
                        shortName: state.shortName,
                        title: state.title,
                        sponsor: state.sponsor,
                        program: state.program === Constants.ObjectId.Empty ? null : state.program,
                        useLatestCentralMonitoring: state.useLatestCentralMonitoring,
                    };
                }
                mutation({
                    variables,
                    refetchQueries: [
                        {
                            query: GET_MANAGE_ORG_STUDIES,
                        },
                    ],
                })
                    .then((res) => {
                        if (typeof res?.data?.saveStudyConfiguration === 'string') {
                            errorRequest();
                            return;
                        }

                        if (
                            (currentStudy && currentStudy?.logo !== state.logo) ||
                            (!currentStudy && state.logo.length)
                        ) {
                            const studyId = !currentStudy ? res.data.saveStudy.id : currentStudy.id;
                            saveStudyLogo({
                                variables: {
                                    studyId,
                                    logo: state.logo?.substring(state.logo.lastIndexOf(',') + 1) || '',
                                },
                                refetchQueries: getRefetchQueries(),
                            })
                                .then(() => {
                                    NavigationService.setUpdateStudies(user?.user.organisationKey, true);
                                    successRequest();
                                })
                                .catch(() => {
                                    errorRequest();
                                });
                        } else {
                            NavigationService.setUpdateStudies(user?.user.organisationKey, true);
                            successRequest();
                        }
                    })
                    .catch(() => {
                        errorRequest();
                    });
            }
        }
    };

    return (
        <CustomDialog
            onClose={() => {
                onClose();
            }}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
        >
            <DialogTitle id="add-study-modal-title">
                {currentStudy ? t('admin:editStudy', { recordId: currentStudy.recordId }) : t('admin:addStudy')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        onClose();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-study-dialog-content">
                <div className="k-mb-2">
                    <FormControl>
                        <Checkbox
                            data-testid="useLatestCentralMonitoring-checkbox"
                            rounded="none"
                            id="useLatestCentralMonitoring-checkbox"
                            className="cell-checkbox no-border"
                            checked={state.useLatestCentralMonitoring}
                            onChange={onChange('useLatestCentralMonitoring')}
                            label={t('admin:useLatestCentralMonitoring')}
                            disabled={isSysAdmin || (currentStudy ? studyHasDataset !== false : false)}
                            size="medium"
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <TextField
                            label={`${t('cm:study_num')}*`}
                            id="add-study-dialog-studyNumber"
                            fullWidth
                            value={state.studyNumber}
                            onChange={onChange('studyNumber')}
                            placeholder={t('admin:enterStudyNum')}
                            helperText={
                                <CharactersRemaining
                                    id="add-study-dialog-studyNumber-help-text"
                                    length={state.studyNumber?.length}
                                    max={20}
                                    error={error.studyNumber}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!error.studyNumber}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <TextField
                            label={`${t('admin:shortName')}*`}
                            id="add-study-dialog-shortName"
                            fullWidth
                            value={state.shortName}
                            onChange={onChange('shortName')}
                            placeholder={t('admin:enterShortName')}
                            helperText={
                                <CharactersRemaining
                                    id="add-study-dialog-shortName-help-text"
                                    length={state.shortName?.length}
                                    max={70}
                                    error={error.shortName}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!error.shortName}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper" style={{ marginBottom: 10 }}>
                    <TextField
                        id="add-study-dialog-title"
                        label={`${t('admin:title')}*`}
                        multiline
                        minRows={4}
                        fullWidth
                        value={state.title}
                        onChange={onChange('title')}
                        size="small"
                        placeholder={t('admin:enterTitle')}
                        helperText={
                            <CharactersRemaining
                                id="add-study-dialog-title-help-text"
                                length={state.title.length}
                                max={500}
                                error={error.title}
                            />
                        }
                        variant="outlined"
                        color="secondary"
                        error={!!error.title}
                        disabled={isSysAdmin}
                    />
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <SelectWithCategories
                            customClassName="edit-study-program-units"
                            showWarning={false}
                            fieldSelect="program"
                            onChange={onChange}
                            label={t('admin:program')}
                            filterable
                            searchable
                            showLabels={false}
                            labelField="program"
                            dataArray={programDataArray}
                            customId="add-study-program"
                            currentState={state.program}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <TextField
                            label={t('admin:sponsor')}
                            id="add-study-dialog-sponsor"
                            fullWidth
                            value={state.sponsor}
                            onChange={onChange('sponsor')}
                            placeholder={t('admin:enterSponsor')}
                            helperText={
                                <CharactersRemaining
                                    id="add-study-dialog-sponsor-help-text"
                                    length={state.sponsor?.length}
                                    max={70}
                                    error={error.sponsor}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            className="before-select mb"
                            error={!!error.sponsor}
                            disabled={isSysAdmin}
                        />
                    </FormControl>
                </div>
                <EditLogo onLogoChange={onLogoChange} logo={state.logo} disabled={isSysAdmin} />

                {currentStudy && (
                    <div className="type-edc-wrapper" style={{ marginBottom: 10, marginTop: 10 }}>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <TextField
                                    id="add-study-dialog-studyId"
                                    label={t('admin:studyId')}
                                    fullWidth
                                    value={state.studyId}
                                    size="small"
                                    slotProps={{
                                        input: {
                                            readOnly: true,
                                            endAdornment: <CopyButton value={state.studyId} />,
                                        },
                                    }}
                                    variant="outlined"
                                    color="secondary"
                                    className="mr"
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <TextField
                                    id="edit-field-config-id"
                                    label={t('admin:configId')}
                                    onChange={onChange('configId')}
                                    fullWidth
                                    value={state.configId}
                                    size="small"
                                    slotProps={{
                                        input: {
                                            readOnly: !isSysAdmin,
                                            endAdornment: <CopyButton value={state.configId ?? ''} />,
                                        },
                                    }}
                                    helperText={
                                        <span style={{ color: '#ae0015' }} className="remaining-text">
                                            {error.configId}
                                        </span>
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!error.configId}
                                />
                            </FormControl>
                        </div>
                    </div>
                )}

                <DialogActions className={`add-study-dialog-content-actions ${!currentStudy ? 'flex-end' : ''}`}>
                    <ModalUpdatedInfo item={currentStudy} />
                    <Button
                        disabled={
                            disabledSaveButton.value ||
                            Object.keys(error).filter((item) => (error as any)[item]).length > 0
                        }
                        className="add-study-btn"
                        text={currentStudy ? 'update' : 'add'}
                        onClick={submit}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default AddStudyDialog;
